import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
 HiOutlinePlus,
 HiOutlineSearch,
 HiOutlineX,
 HiOutlineChevronLeft,
 HiOutlineUserGroup,
 HiOutlineTrash,
 HiOutlineUpload,
 HiOutlineDownload,
 HiOutlineDocumentText,
} from "react-icons/hi";
import { createUser, updateUser, fetchUsers, fetchUserById, deleteUser, bulkDeleteUsers, updateUserStatus, bulkUploadUsers } from "../services/userManagementApi";
import { fetchMasterData, fetchSchools, fetchStateById } from "../services/masterDataApi";
import {
 fetchMyStates, fetchMyDistricts, fetchMyBlocks, fetchMySchools,
 assignStateToSPL, removeStateFromSPL,
 fetchSPLStates, fetchPLDistricts, fetchPMBlocks, fetchPOSchools,
 assignDistrictsToPL, removeDistrictsFromPL,
 assignBlocksToPM, removeBlocksFromPM,
 assignSchoolsToPO, removeSchoolsFromPO, checkSchoolAvailability,
} from "../services/assignmentApi";
import SearchableDropdown, { scopedStatesFetchFn, staticFetchFn } from "../components/SearchableDropdown";
import ChipMultiSelect from "../components/ChipMultiSelect";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import { useDebounce } from "../hooks/useDebounce";
import { useUserRole } from "../hooks/useUserRole";

const ROLE_OPTIONS = ["ADMIN", "SPL", "PL", "PM", "PO"];
// State/District/Block/School are independent AND filters here — none of
// them narrow another's option list or depend on the selected Role. Picking
// a combination that doesn't correspond to any real user (e.g. a District
// alongside a School under a different block) just yields no results.
const EMPTY_USER_FILTERS = { role: "", enabled: "", state_id: "", district_id: "", block_id: "", school_id: "" };

const ROLE_COLORS = {
 ADMIN: "bg-red-50 text-red-700",
 SPL: "bg-blue-50 text-blue-700",
 PL: "bg-indigo-50 text-indigo-700",
 PM: "bg-amber-50 text-amber-700",
 PO: "bg-green-50 text-green-700",
};

const initialForm = {
 first_name: "",
 last_name: "",
 username: "",
 email: "",
 password: "",
 role: "PO",
 state_id: "",
 district_id: "",
 block_id: "",
 district_ids: [], // multi-select for PL assignment, and PM's district filter
 block_ids: [], // multi-select for PM assignment
 school_ids: [], // multi-select for PO assignment (adopted schools only)
};

function Users() {
 const { role: userRole, canCreateUsers, allowedRoles, canManageUser, canBulkUploadUsers, userProfile } = useUserRole();
 /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
    reopens the same user instead of dropping back to the list ── */
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit");
 const setView = useCallback((v, params = {}) => {
 if (v === "list") {
 setSearchParams({});
 } else if (v === "form" && params.edit) {
 setSearchParams({ view: "form", edit: params.edit });
 } else {
 setSearchParams({ view: v });
 }
 }, [setSearchParams]);
 const [data, setData] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editingUserId, setEditingUserId] = useState(null); // null = create, id = edit
 const [originalRole, setOriginalRole] = useState(null);
 const [form, setForm] = useState(initialForm);
 const [submitting, setSubmitting] = useState(false);
 const [search, setSearch] = useState("");
 const debouncedSearch = useDebounce(search, 400);
 const [toast, setToast] = useState(null);
 const [formErrors, setFormErrors] = useState({});
 const [selectedIds, setSelectedIds] = useState([]);
 const [showDeleteModal, setShowDeleteModal] = useState(false);
 const [deleteTarget, setDeleteTarget] = useState(null); // null = bulk, string = single user id
 const [deleting, setDeleting] = useState(false);
 const [togglingStatusId, setTogglingStatusId] = useState(null);

 // Bulk Upload PM state
 const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
 const [bulkUploadFile, setBulkUploadFile] = useState(null);
 const [bulkUploading, setBulkUploading] = useState(false);
 const [bulkUploadResult, setBulkUploadResult] = useState(null);

 // Pagination state
 const [currentPage, setCurrentPage] = useState(1);
 const [totalCount, setTotalCount] = useState(0);
 const [totalPages, setTotalPages] = useState(1);
 const [pageSize, setPageSize] = useState(20);

 // Filter state
 const [filters, setFilters] = useState(EMPTY_USER_FILTERS);

 // Location dropdown options for user creation
 const [statesList, setStatesList] = useState([]);
 const [districtsList, setDistrictsList] = useState([]);
 const [blocksList, setBlocksList] = useState([]); // PM/PO: union across form.district_ids
 const [schoolsList, setSchoolsList] = useState([]); // PO's school multi-select (adopted only): union across form.block_ids
 const [schoolsByBlock, setSchoolsByBlock] = useState({}); // cache: block_id -> schools, so toggling one block doesn't refetch every already-fetched block
 const [assignedSchools, setAssignedSchools] = useState([]); // PO being edited: their currently assigned schools, merged into schoolsList even if no longer is_adopted so existing chips still render

 // Full, unscoped lists for the list-view Filters panel. Unlike districtsList/
 // blocksList/schoolsList above (which are scoped to whatever the create/edit
 // form's own selections narrow them to), these are independent of each other
 // and of Role — every District/Block/School shows up regardless of what else
 // is selected, matching the Filters panel's independent-AND design.
 // District/Block/School filter options all load lazily via async-select's
 // own fetchFn (below) instead of being fetched in full up front — all three
 // are nationwide, unbounded-size lists (same reason School's eager fetch was
 // the actual cause of the multi-second /school/schools/fetch calls on this
 // page's load; District/Block just hadn't grown large enough yet to notice).

 // Live (pre-Apply) draft of the Filters panel — District/Block/School below
 // scope their own fetchFn to whatever State/District/Block is picked above
 // them (e.g. State=Uttar Pradesh -> District only offers UP's districts).
 // This only scopes the choices on offer, it doesn't require picking a
 // parent first or make the filters dependent on each other for the actual
 // query.
 const [filterDraft, setFilterDraft] = useState({});

 // Search + reveal state for the three inline assignment pickers now lives inside
 // ChipMultiSelect itself (frontend/webadmin/src/components/ChipMultiSelect.js).
 const [creatorStates, setCreatorStates] = useState(null);
 const [creatorDistricts, setCreatorDistricts] = useState(null);
 const [creatorBlocks, setCreatorBlocks] = useState(null);

 // Original assignment-table scope for the user being edited, used to diff against
 // the newly-selected picker values so we only call add/remove for what changed.
 const [originalAssignment, setOriginalAssignment] = useState({ stateIds: [], districtIds: [], blockIds: [], schoolIds: [] });

 // Guard so that programmatic population of state_id during edit-mode doesn't get
 // immediately clobbered by the "reset downstream fields on state change" effect below.
 // State id (string) the code itself just put into form.state_id while
 // pre-filling the edit form. The state effect below skips its "clear the
 // downstream selections" reset only for that exact value, so a later manual
 // change to a different state always clears them. (A plain boolean flag
 // stayed armed whenever the pre-filled value didn't change, and then swallowed
 // the user's first real state change.)
 const programmaticStateRef = useRef(null);

 // Load states on mount and when modal opens. Every page is fetched, not just
 // the first: this list backs the list-page State filter and the scoped
 // State pickers for SPL/PL/PM creators, so a state past the first page
 // would otherwise be missing from both.
 // States don't change from this page, so once loaded the form-open calls
 // below reuse the list instead of re-fetching it.
 const statesLoadedRef = useRef(false);
 const loadStates = useCallback(() => {
  if (statesLoadedRef.current) return;
  statesLoadedRef.current = true;
  const PAGE_SIZE = 1000;
  const loadAll = async () => {
   const all = [];
   for (let page = 1; ; page += 1) {
    const r = await fetchMasterData("states", { page, page_size: PAGE_SIZE });
    const rows = r.data || [];
    all.push(...rows);
    if (rows.length < PAGE_SIZE || all.length >= (r.total_count ?? all.length)) break;
   }
   return all;
  };
  loadAll()
   .then(setStatesList)
   .catch((err) => {
    statesLoadedRef.current = false;
    console.error("Failed to load states", err);
   });
 }, []);

 // Resolves a pre-selected State's label by id when editing a user, so it
 // shows even when that state isn't on the dropdown's first search page.
 // Uses the already-loaded states list first; only fetches by id if missing.
 const resolveStateLabel = useCallback(
  (id) => {
   const found = statesList.find((st) => String(st.id) === String(id));
   if (found) return Promise.resolve({ id: found.id, name: found.name });
   return fetchStateById(id).then((r) => (r?.data ? { id: r.data.id, name: r.data.name } : null));
  },
  [statesList]
 );

 useEffect(() => {
  loadStates();
 }, [loadStates]);

 // Load creator's own scope once on mount (used to filter dropdowns in the create form)
 useEffect(() => {
  if (!userRole) return;
  if (userRole === "SPL") {
   fetchMyStates().then(setCreatorStates).catch(() => setCreatorStates([]));
  } else if (userRole === "PL") {
   fetchMyDistricts().then(setCreatorDistricts).catch(() => setCreatorDistricts([]));
  } else if (userRole === "PM") {
   fetchMyBlocks().then(setCreatorBlocks).catch(() => setCreatorBlocks([]));
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [userRole]);

 // Load districts when state changes (scoped for PL/PM creators)
 useEffect(() => {
  if (form.state_id) {
   if (userRole === "PL" && creatorDistricts) {
    setDistrictsList(creatorDistricts.filter((d) => String(d.state_id) === String(form.state_id)));
   } else if (userRole === "PM" && creatorBlocks) {
    const seen = new Set();
    const pmDistricts = creatorBlocks
     .filter((b) => String(b.state_id) === String(form.state_id))
     .filter((b) => { if (seen.has(b.district_id)) return false; seen.add(b.district_id); return true; })
     .map((b) => ({ id: b.district_id, name: b.district_name }));
    setDistrictsList(pmDistricts);
   } else {
    fetchMasterData("districts", { page: 1, page_size: 100, state_id: Number.parseInt(form.state_id, 10) }).then((r) => setDistrictsList(r.data || [])).catch(() => {});
   }
  } else {
   setDistrictsList([]);
  }
  // Skip the downstream reset once when we've just programmatically populated
  // state_id (+ district_id/block_id/etc in the same update) from edit-mode data.
  const programmatic = programmaticStateRef.current !== null
   && String(form.state_id ?? "") === programmaticStateRef.current;
  programmaticStateRef.current = null;
  if (!programmatic) {
   setForm((prev) => ({ ...prev, district_id: "", block_id: "", district_ids: [], block_ids: [], school_ids: [] }));
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [form.state_id, userRole, creatorDistricts, creatorBlocks]);

 // Load blocks for the "Assign Blocks" multi-select (PM and PO both use this now) —
 // union across every district in form.district_ids, deduped by block id, so picking
 // several districts shows all their blocks together instead of just the last one picked.
 useEffect(() => {
  if (form.role !== "PM" && form.role !== "PO") return;
  if (!form.district_ids.length) {
   setBlocksList([]);
   return;
  }
  if (userRole === "PM" && creatorBlocks) {
   setBlocksList(creatorBlocks.filter((b) => form.district_ids.includes(b.district_id)));
   return;
  }
  let cancelled = false;
  (async () => {
   const lists = await Promise.all(
    form.district_ids.map((did) =>
     fetchMasterData("blocks", { page: 1, page_size: 100, district_id: did })
      .then((r) => r.data || [])
      .catch(() => [])
    )
   );
   if (cancelled) return;
   const seen = new Set();
   const merged = lists.flat().filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));
   setBlocksList(merged);
  })();
  return () => { cancelled = true; };
 }, [form.district_ids, form.role, userRole, creatorBlocks]);

 // Fetch schools per block, cached — only blocks not already in schoolsByBlock
 // trigger a request, so toggling one block in the multi-select fetches just
 // that one block instead of re-fetching every already-selected block.
 useEffect(() => {
  if (form.role !== "PO") return;
  const missing = form.block_ids.filter((bid) => !schoolsByBlock[bid]);
  if (missing.length === 0) return;
  let cancelled = false;
  (async () => {
   const results = await Promise.all(
    missing.map((bid) =>
     fetchSchools({ page: 1, page_size: 100, block_id: bid, is_adopted: true })
      .then((r) => r.data || [])
      .catch(() => [])
    )
   );
   if (cancelled) return;
   setSchoolsByBlock((prev) => {
    const next = { ...prev };
    missing.forEach((bid, i) => { next[bid] = results[i]; });
    return next;
   });
  })();
  return () => { cancelled = true; };
 }, [form.block_ids, form.role, schoolsByBlock]);

 // Load adopted schools for PO's "Assign Schools" multi-select — union across every
 // block in form.block_ids, deduped by school id, from the per-block cache above.
 // Only is_adopted (continuous-cycle) schools are eligible — the server rejects
 // quarterly schools for PO assignment with a 400.
 useEffect(() => {
  if (form.role !== "PO" || !form.block_ids.length) {
   setSchoolsList([]);
   return;
  }
  const seen = new Set();
  const merged = [...assignedSchools, ...form.block_ids.flatMap((bid) => schoolsByBlock[bid] || [])]
   .filter((s) => form.block_ids.includes(s.block_id))
   .filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true)));
  setSchoolsList(merged);
 }, [form.block_ids, form.role, schoolsByBlock, assignedSchools]);

 const loadData = useCallback(async (page = currentPage, searchQuery = debouncedSearch, activeFilters = filters) => {
 setLoading(true);
 try {
 const params = { page, page_size: pageSize, search: searchQuery };
 if (activeFilters.role) params.role = activeFilters.role;
 if (activeFilters.enabled !== "" && activeFilters.enabled !== undefined) params.enabled = activeFilters.enabled;
 if (activeFilters.state_id) params.state_id = activeFilters.state_id;
 if (activeFilters.district_id) params.district_id = activeFilters.district_id;
 if (activeFilters.block_id) params.block_id = activeFilters.block_id;
 if (activeFilters.school_id) params.school_id = activeFilters.school_id;
 const result = await fetchUsers(params);
 setData(result.data);
 setTotalCount(result.total_count);
 setTotalPages(result.total_pages);
 setCurrentPage(result.current_page);
 } catch (err) {
 console.error("Failed to load users", err);
 setToast({ message: "Failed to load users.", type: "error" });
 } finally {
 setLoading(false);
 }
 }, [currentPage, debouncedSearch, filters, pageSize]);

 useEffect(() => {
 setCurrentPage(1);
 loadData(1, debouncedSearch, filters);
 }, [debouncedSearch, filters, pageSize]);

 const handlePageSizeChange = (newSize) => {
 setPageSize(newSize);
 setCurrentPage(1);
 };

 // Selection helpers

 const toggleSelect = (id) => {
 setSelectedIds((prev) =>
 prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
 );
 };

 const toggleSelectAll = () => {
 const manageableIds = data.filter((u) => canManageUser(u.role)).map((u) => u.id);
 const allSelected = manageableIds.length > 0 && manageableIds.every((id) => selectedIds.includes(id));
 if (allSelected) {
 setSelectedIds((prev) => prev.filter((id) => !manageableIds.includes(id)));
 } else {
 setSelectedIds((prev) => [...new Set([...prev, ...manageableIds])]);
 }
 };

 const isAllSelected =
 data.filter((u) => canManageUser(u.role)).length > 0 &&
 data.filter((u) => canManageUser(u.role)).every((u) => selectedIds.includes(u.id));

 // Delete handlers

 const openDeleteSingle = (userId) => {
 setDeleteTarget(userId);
 setShowDeleteModal(true);
 };

 const openDeleteBulk = () => {
 setDeleteTarget(null);
 setShowDeleteModal(true);
 };

 const confirmDelete = async () => {
 setDeleting(true);
 try {
 if (deleteTarget) {
 // Single delete
 await deleteUser(deleteTarget);
 setToast({ message: "User deleted successfully.", type: "success" });
 } else {
 // Bulk delete
 const result = await bulkDeleteUsers(selectedIds);
 if (result.failed && result.failed.length > 0) {
 setToast({
 message: `Deleted ${result.deleted.length} user(s). ${result.failed.length} failed.`,
 type: "error",
 });
 } else {
 setToast({
 message: `${result.deleted.length} user(s) deleted successfully.`,
 type: "success",
 });
 }
 }
 setSelectedIds([]);
 setShowDeleteModal(false);
 setDeleteTarget(null);
 if (view === "form" && deleteTarget) {
 setView("list");
 setEditingUserId(null);
 }
 loadData(currentPage, debouncedSearch);
 } catch (err) {
 console.error("Delete failed", err);
 const detail =
 err.response?.data?.detail || "Failed to delete user(s). Please try again.";
 setToast({ message: detail, type: "error" });
 } finally {
 setDeleting(false);
 }
 };

 // Status toggle handler

 const handleToggleStatus = async (userId, currentEnabled) => {
 const newEnabled = !currentEnabled;
 setTogglingStatusId(userId);
 try {
 await updateUserStatus(userId, newEnabled);
 setData((prev) =>
 prev.map((u) => (u.id === userId ? { ...u, enabled: newEnabled } : u))
 );
 setToast({
 message: `User ${newEnabled ? "activated" : "deactivated"} successfully.`,
 type: "success",
 });
 } catch (err) {
 console.error("Status toggle failed", err);
 const detail =
 err.response?.data?.detail || "Failed to update user status.";
 setToast({ message: detail, type: "error" });
 } finally {
 setTogglingStatusId(null);
 }
 };

 const openCreate = () => {
 programmaticStateRef.current = null;
 setEditingUserId(null);
 setAssignedSchools([]);
 const newForm = { ...initialForm, role: allowedRoles[allowedRoles.length - 1] || "PO" };
 // Auto-set state when creator has exactly one option
 if (userRole === "SPL" && creatorStates?.length === 1) {
  newForm.state_id = String(creatorStates[0].id);
 } else if (userRole === "PL" && creatorDistricts?.length > 0) {
  const stateIds = [...new Set(creatorDistricts.map((d) => d.state_id))];
  if (stateIds.length === 1) newForm.state_id = String(stateIds[0]);
 } else if (userRole === "PM" && creatorBlocks?.length > 0) {
  const stateIds = [...new Set(creatorBlocks.map((b) => b.state_id))];
  if (stateIds.length === 1) newForm.state_id = String(stateIds[0]);
 }
 setForm(newForm);
 setFormErrors({});
 loadStates();
 setView("form");
 };

 const openEditModal = async (user) => {
 // A direct click already has the full item — mark the URL-restore effect
 // as satisfied so it doesn't also redundantly re-fetch this same item.
 hasRestoredFromUrl.current = true;
 setEditingUserId(user.id);
 setOriginalRole(user.role || null);
 setOriginalAssignment({ stateIds: [], districtIds: [], blockIds: [], schoolIds: [], stateId: user.state_id ? String(user.state_id) : "" });
 setAssignedSchools([]);
 // The base form below sets state_id (+ others) in one update — guard the cascading
 // "reset downstream fields on state change" effect so it doesn't immediately clobber
 // these pre-filled values before the async assignment-table fetch runs.
 programmaticStateRef.current = user.state_id ? String(user.state_id) : "";
 setForm({
  first_name: user.first_name || "",
  last_name: user.last_name || "",
  username: user.username || "",
  email: user.email || "",
  password: "",
  role: user.role || "",
  // The API derives these from the user's assignment rows (null when unset or
  // when the rows disagree); the pickers below are filled from the assignment
  // tables themselves.
  state_id: user.state_id ? String(user.state_id) : "",
  district_id: user.district_id ? String(user.district_id) : "",
  block_id: user.block_id ? String(user.block_id) : "",
  district_ids: [],
  block_ids: [],
  school_ids: [],
 });
 setFormErrors({});
 loadStates();
 setView("form", { edit: user.id });

 // Pre-populate the assignment pickers from the REAL assignment tables (source of
 // truth), not from the flat profile columns.
 //
 // Same self-vs-other permission split as the Detail view's assigned-scope fetch: the
 // manager-facing lookup endpoints (fetchSPLStates/fetchPLDistricts/fetchPMBlocks/
 // fetchPOSchools) are gated by permissions meant for someone with authority OVER that
 // role, so a user editing their OWN record must use the self-view endpoints instead.
 const isSelf = !!userProfile?.id && userProfile.id === user.id;
 try {
  if (user.role === "SPL") {
   const states = await (isSelf ? fetchMyStates() : fetchSPLStates(user.id));
   const stateIds = (states || []).map((s) => s.id);
   setOriginalAssignment((prev) => ({ ...prev, stateIds }));
   const stateId = states?.[0] ? String(states[0].id) : "";
   programmaticStateRef.current = stateId;
   setOriginalAssignment((prev) => ({ ...prev, stateId }));
   setForm((prev) => ({ ...prev, state_id: stateId }));
  } else if (user.role === "PL") {
   const districts = await (isSelf ? fetchMyDistricts() : fetchPLDistricts(user.id));
   const districtIds = (districts || []).map((d) => d.id);
   setOriginalAssignment((prev) => ({ ...prev, districtIds }));
   // A PL with no districts has no location yet — the State field starts blank.
   const stateId = districts?.[0] ? String(districts[0].state_id) : "";
   programmaticStateRef.current = stateId;
   setOriginalAssignment((prev) => ({ ...prev, stateId }));
   setForm((prev) => ({
    ...prev,
    state_id: stateId,
    district_ids: districtIds,
   }));
  } else if (user.role === "PM") {
   const blocks = await (isSelf ? fetchMyBlocks() : fetchPMBlocks(user.id));
   const blockIds = (blocks || []).map((b) => b.id);
   // A PM's blocks can already span multiple districts (bulk upload, or just
   // built up over time) — load every one of them, not just the first block's
   // district, so the "Assign Districts" picker reflects the real scope.
   const districtIds = [...new Set((blocks || []).map((b) => b.district_id))];
   setOriginalAssignment((prev) => ({ ...prev, blockIds }));
   const stateId = blocks?.[0] ? String(blocks[0].state_id) : (user.state_id ? String(user.state_id) : "");
   programmaticStateRef.current = stateId;
   setOriginalAssignment((prev) => ({ ...prev, stateId }));
   setForm((prev) => ({
    ...prev,
    state_id: stateId,
    district_ids: districtIds,
    block_ids: blockIds,
   }));
  } else if (user.role === "PO") {
   const schools = await (isSelf ? fetchMySchools() : fetchPOSchools(user.id));
   const schoolIds = (schools || []).map((s) => s.id);
   const blockIds = [...new Set((schools || []).map((s) => s.block_id).filter(Boolean))];
   setOriginalAssignment((prev) => ({ ...prev, schoolIds }));
   setAssignedSchools(schools || []);
   // The API returns every district the PO's schools are in (derived from the
   // assignment rows), so no block lookup is needed to fill this picker.
   const districtIds = user.district_ids || [];
   setForm((prev) => ({ ...prev, district_ids: districtIds, block_ids: blockIds, school_ids: schoolIds }));
  }
 } catch (err) {
  console.error("Failed to load assignment scope for user", err);
 }
 };

 // Restore edit view from URL on refresh — the list row only has a subset of
 // fields, so re-fetch the full user (same endpoint used elsewhere) and feed
 // it through the normal openEditModal path.
 const hasRestoredFromUrl = useRef(false);
 useEffect(() => {
 if (hasRestoredFromUrl.current) return;
 if (view === "form" && urlEditId) {
 hasRestoredFromUrl.current = true;
 fetchUserById(urlEditId)
 .then((res) => openEditModal(res.data))
 .catch(() => setView("list"));
 } else if (view === "form" && !urlEditId) {
 hasRestoredFromUrl.current = true;
 openCreate();
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId]);

 const handleChange = (field, value) => {
 if (field === "role") {
  // Reset all location fields when role changes to avoid state from a previous role
  setForm((prev) => {
  const reset = { ...prev, role: value, state_id: "", district_id: "", block_id: "", district_ids: [], block_ids: [], school_ids: [] };
  // Re-lock SPL's state when switching to a role that shows state (SPL or PL)
  if (userRole === "SPL" && creatorStates?.length === 1 && (value === "SPL" || value === "PL" || value === "PM" || value === "PO")) {
   reset.state_id = String(creatorStates[0].id);
  }
  return reset;
  });
 } else {
  setForm((prev) => ({ ...prev, [field]: value }));
 }
 if (formErrors[field]) {
 setFormErrors((prev) => {
 const next = { ...prev };
 delete next[field];
 return next;
 });
 }
 };

 const NAME_RE = /^[A-Za-z][A-Za-z .'-]*$/;
 // Matches _BULK_EMAIL_RE in services/user_management/app/api/common.py —
 // a plain \S+@\S+\.\S+ check lets through garbage local parts like "##-1".
 const EMAIL_RE = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

 const validate = () => {
 const errors = {};
 if (!form.first_name.trim()) errors.first_name = "First name is required";
 else if (!NAME_RE.test(form.first_name.trim())) errors.first_name = "First name must contain only letters";
 if (!form.last_name.trim()) errors.last_name = "Last name is required";
 else if (!NAME_RE.test(form.last_name.trim())) errors.last_name = "Last name must contain only letters";
 if (!editingUserId && !form.username.trim()) errors.username = "Username is required";
 if (!form.email.trim()) errors.email = "Email is required";
 else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Invalid email format";
 if (!editingUserId) {
 if (!form.password) errors.password = "Password is required";
 else if (form.password.length < 6) errors.password = "Password must be at least 6 characters";
 }
 if (!form.role) errors.role = "Role is required";
 // Location is mandatory to the depth each role manages: SPL->state,
 // PL->district, PM->block, PO->school. ADMIN needs none. This mirrors
 // the equivalent bulk-upload check (_bulk_assert_required_location in
 // services/user_management/app/api/common.py) so the manual and bulk
 // paths enforce the same rule.
 if (form.role === "SPL" && !form.state_id) errors.state_id = "State is required";
 if (form.role === "PL") {
 if (!form.state_id) errors.state_id = "State is required";
 if (!form.district_ids?.length) errors.district_ids = "At least one district is required";
 }
 if (form.role === "PM") {
 if (!form.state_id) errors.state_id = "State is required";
 if (!form.district_ids?.length) errors.district_ids = "At least one district is required";
 if (!form.block_ids?.length) errors.block_ids = "At least one block is required";
 }
 if (form.role === "PO") {
 if (!form.state_id) errors.state_id = "State is required";
 if (!form.district_ids?.length) errors.district_ids = "At least one district is required";
 if (!form.block_ids?.length) errors.block_ids = "At least one block is required";
 if (!form.school_ids?.length) errors.school_ids = "At least one school is required";
 }
 // Every picked district/block/school must belong to the state/districts/
 // blocks currently selected above it (the lists below are loaded for those
 // parents). Otherwise the form shows one state while the assignment that
 // would be saved is somewhere else — the backend never sees the State field,
 // it derives location from the assignment itself, so catch it here.
 const outside = (ids, list) => {
  const known = new Set((list || []).map((x) => x.id));
  return (ids || []).filter((id) => !known.has(id));
 };
 // Only when the location part of the form was touched (always on create) —
 // a name-only edit must not be blocked by assignments that sit outside the
 // editor's own pick-lists.
 const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));
 const locationTouched = !editingUserId
  || String(form.state_id || "") !== String(originalAssignment.stateId || "")
  || (form.role === "PL" && !sameIds(form.district_ids || [], originalAssignment.districtIds))
  || (form.role === "PM" && !sameIds(form.block_ids || [], originalAssignment.blockIds))
  || (form.role === "PO" && !sameIds(form.school_ids || [], originalAssignment.schoolIds));
 if (!locationTouched) return errors;
 if (["PL", "PM", "PO"].includes(form.role) && form.state_id && !errors.district_ids) {
  const bad = outside(form.district_ids, districtsList);
  if (bad.length) errors.district_ids = `${bad.length} selected district(s) are not in the selected state — re-select the districts.`;
 }
 if (["PM", "PO"].includes(form.role) && !errors.district_ids && !errors.block_ids) {
  const bad = outside(form.block_ids, blocksList);
  if (bad.length) errors.block_ids = `${bad.length} selected block(s) are not in the selected district(s) — re-select the blocks.`;
 }
 if (form.role === "PO" && !errors.block_ids && !errors.school_ids) {
  const bad = outside(form.school_ids, schoolsList);
  if (bad.length) errors.school_ids = `${bad.length} selected school(s) are not in the selected block(s) — re-select the schools.`;
 }
 return errors;
 };

 const isLocationComplete = () => {
 if (form.role === "SPL") return !!form.state_id;
 if (form.role === "PL") return !!form.state_id && form.district_ids?.length > 0;
 if (form.role === "PM") return !!form.state_id && form.district_ids?.length > 0 && form.block_ids?.length > 0;
 if (form.role === "PO") {
 return !!form.state_id && form.district_ids?.length > 0 && form.block_ids?.length > 0 && form.school_ids?.length > 0;
 }
 return true; // ADMIN — no location required
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 const errors = validate();
 if (Object.keys(errors).length > 0) {
 setFormErrors(errors);
 return;
 }

 setSubmitting(true);
 try {
 if (editingUserId) {
  const roleChanged = form.role && form.role !== originalRole;

  // For PO, validate any newly-added schools BEFORE saving anything — a
  // conflicting school should block the whole edit (same as create),
  // instead of applying the profile changes while the assignment silently
  // fails afterward.
  if (!roleChanged && form.role === "PO") {
  const toAdd = form.school_ids.filter((id) => !originalAssignment.schoolIds.includes(id));
  if (toAdd.length > 0) {
   let availability;
   try {
   availability = await checkSchoolAvailability(toAdd);
   } catch (e) {
   console.warn("School availability check failed:", e);
   setToast({ message: "Could not validate school selection. Please try again.", type: "error" });
   return;
   }
   if (availability?.conflicts?.length > 0) {
   const parts = availability.conflicts.map((c) => {
    const schoolName = schoolsList.find((s) => s.id === c.school_id)?.name || `School #${c.school_id}`;
    return `${schoolName} is already assigned to ${c.assigned_to_name || "another PO"}`;
   });
   setToast({
    message: `Cannot save changes: ${parts.join("; ")}. Uncheck the conflicting school(s) or pick a different one and try again.`,
    type: "error",
   });
   return;
   }
  }
  }

  await updateUser(editingUserId, {
  first_name: form.first_name.trim(),
  last_name: form.last_name.trim(),
  email: form.email.trim(),
  ...(roleChanged ? { role: form.role } : {}),
  // No location here — a user's location is their assignment rows, synced
  // below via the assignment-table API calls.
  });

  // Sync the real assignment-table scope (only when the role hasn't changed —
  // a role change means the old assignment rows no longer apply to this user).
  let assignmentSyncFailed = false;
  let assignmentSyncError = null;
  // Remove before add: all of a user's assignments must be in one state, so
  // moving to a new state only validates once the old rows are gone. If the
  // add then fails, put the removed rows back so a failed edit never leaves
  // the user with fewer assignments than before.
  const syncAssignments = async (toAdd, toRemove, add, remove) => {
   if (toRemove.length > 0) await remove(editingUserId, toRemove);
   if (toAdd.length === 0) return;
   try {
    await add(editingUserId, toAdd);
   } catch (e) {
    if (toRemove.length > 0) {
     try {
      await add(editingUserId, toRemove);
      e.previousRestored = true;
     } catch (restoreErr) {
      console.warn("Could not restore previous assignments:", restoreErr);
      e.previousRestored = false;
     }
    }
    throw e;
   }
  };
  if (!roleChanged) {
  try {
   if (form.role === "SPL") {
   // Replace semantics: resending the full new set is enough; only call remove
   // if the admin cleared the state entirely.
   if (form.state_id) {
    await assignStateToSPL(editingUserId, [parseInt(form.state_id, 10)]);
   } else if (originalAssignment.stateIds.length > 0) {
    await removeStateFromSPL(editingUserId, originalAssignment.stateIds);
   }
   } else if (form.role === "PL") {
   const toAdd = form.district_ids.filter((id) => !originalAssignment.districtIds.includes(id));
   const toRemove = originalAssignment.districtIds.filter((id) => !form.district_ids.includes(id));
   await syncAssignments(toAdd, toRemove, assignDistrictsToPL, removeDistrictsFromPL);
   } else if (form.role === "PM") {
   const toAdd = form.block_ids.filter((id) => !originalAssignment.blockIds.includes(id));
   const toRemove = originalAssignment.blockIds.filter((id) => !form.block_ids.includes(id));
   await syncAssignments(toAdd, toRemove, assignBlocksToPM, removeBlocksFromPM);
   } else if (form.role === "PO") {
   const toAdd = form.school_ids.filter((id) => !originalAssignment.schoolIds.includes(id));
   const toRemove = originalAssignment.schoolIds.filter((id) => !form.school_ids.includes(id));
   await syncAssignments(toAdd, toRemove, assignSchoolsToPO, removeSchoolsFromPO);
   }
  } catch (e) {
   console.warn("Assignment scope update failed:", e);
   assignmentSyncFailed = true;
   assignmentSyncError = e;
  }
  }

  setView("list");
  setEditingUserId(null);
  setOriginalRole(null);

  let assignmentErrorMessage = "User updated, but assignment scope could not be fully saved.";
  const rawAssignmentDetail = assignmentSyncError?.response?.data?.detail;
  if (rawAssignmentDetail && typeof rawAssignmentDetail === "object" && Array.isArray(rawAssignmentDetail.conflicts)) {
   const parts = rawAssignmentDetail.conflicts.map((c) => {
    const schoolName = schoolsList.find((s) => s.id === c.school_id)?.name || `School #${c.school_id}`;
    return `${schoolName} is already assigned to ${c.assigned_to_name || "another PO"}`;
   });
   assignmentErrorMessage = `User updated, but: ${parts.join("; ")}`;
  } else if (typeof rawAssignmentDetail === "string") {
   assignmentErrorMessage = `User updated, but assignment update failed: ${rawAssignmentDetail}`;
  }
  if (assignmentSyncError?.previousRestored === true) {
   assignmentErrorMessage += " No assignment changes were saved — the previous assignments were kept.";
  } else if (assignmentSyncError?.previousRestored === false) {
   assignmentErrorMessage += " Warning: the previous assignments were removed and could not be put back — please re-assign them.";
  }

  setToast(
   assignmentSyncFailed
    ? { message: assignmentErrorMessage, type: "error" }
    : { message: "User updated successfully.", type: "success" }
  );
  loadData(currentPage, debouncedSearch);
 } else {
  // PO schools are exclusive (one PO per school per cycle) and can only be
  // assigned after the account exists — validate the selection up front so
  // a conflicting school blocks creation instead of surfacing afterward.
  if (form.role === "PO" && form.school_ids?.length > 0) {
  let availability;
  try {
   availability = await checkSchoolAvailability(form.school_ids);
  } catch (e) {
   console.warn("School availability check failed:", e);
   setToast({ message: "Could not validate school selection. Please try again.", type: "error" });
   return;
  }
  if (availability?.conflicts?.length > 0) {
   const parts = availability.conflicts.map((c) => {
   const schoolName = schoolsList.find((s) => s.id === c.school_id)?.name || `School #${c.school_id}`;
   return `${schoolName} is already assigned to ${c.assigned_to_name || "another PO"}`;
   });
   setToast({
   message: `Cannot create user: ${parts.join("; ")}. Uncheck the conflicting school(s) or pick a different one and try again.`,
   type: "error",
   });
   return;
  }
  }

  // Location is not sent: it is set by the assignment calls below. block_id
  // only tells the backend which block's PM(s) to notify about the new user.
  const notifyBlockId = form.role === "PO" ? (form.block_ids[0] ?? null) : null;
  const result = await createUser({
  username: form.username.trim(),
  email: form.email.trim(),
  first_name: form.first_name.trim(),
  last_name: form.last_name.trim(),
  password: form.password,
  role: form.role,
  block_id: notifyBlockId,
  });
  // Post-creation assignment for SPL (state), PL (districts), PM (blocks), PO (schools)
  const newUserId = result?.data?.keycloak_user_id;
  let assignmentSyncFailed = false;
  let assignmentSyncError = null;
  if (newUserId) {
  try {
   if (form.role === "SPL" && form.state_id) {
   await assignStateToSPL(newUserId, [parseInt(form.state_id, 10)]);
   } else if (form.role === "PL" && form.district_ids?.length > 0) {
   await assignDistrictsToPL(newUserId, form.district_ids);
   } else if (form.role === "PM" && form.block_ids?.length > 0) {
   await assignBlocksToPM(newUserId, form.block_ids);
   } else if (form.role === "PO" && form.school_ids?.length > 0) {
   await assignSchoolsToPO(newUserId, form.school_ids);
   }
  } catch (e) {
   console.warn("Post-creation assignment failed:", e);
   assignmentSyncFailed = true;
   assignmentSyncError = e;
  }
  }
  setView("list");
  setForm(initialForm);

  let assignmentErrorMessage = "User created, but assignment could not be fully saved.";
  const rawAssignmentDetail = assignmentSyncError?.response?.data?.detail;
  if (rawAssignmentDetail && typeof rawAssignmentDetail === "object" && Array.isArray(rawAssignmentDetail.conflicts)) {
  const parts = rawAssignmentDetail.conflicts.map((c) => {
   const schoolName = schoolsList.find((s) => s.id === c.school_id)?.name || `School #${c.school_id}`;
   return `${schoolName} is already assigned to ${c.assigned_to_name || "another PO"}`;
  });
  assignmentErrorMessage = `User created, but: ${parts.join("; ")}`;
  } else if (rawAssignmentDetail && typeof rawAssignmentDetail === "object" && typeof rawAssignmentDetail.message === "string") {
  assignmentErrorMessage = `User created, but: ${rawAssignmentDetail.message}`;
  } else if (typeof rawAssignmentDetail === "string") {
  assignmentErrorMessage = `User created, but assignment failed: ${rawAssignmentDetail}`;
  }

  setToast(
  assignmentSyncFailed
   ? { message: assignmentErrorMessage, type: "error" }
   : { message: `User '${form.username}' created successfully.`, type: "success" }
  );
  loadData(currentPage, debouncedSearch);
 }
 } catch (err) {
 console.error(editingUserId ? "Update user failed" : "Create user failed", err);
 const rawDetail = err.response?.data?.detail;
 const detail = Array.isArray(rawDetail)
  ? rawDetail.map((e) => e.msg || JSON.stringify(e)).join("; ")
  : typeof rawDetail === "string"
  ? rawDetail
  : editingUserId ? "Failed to update user." : "Failed to create user. Please try again.";
 if (err.response?.status === 409) {
 if (detail.toLowerCase().includes("email")) {
  setFormErrors({ email: "Email already exists" });
 } else {
  setFormErrors({ username: "Username already exists" });
 }
 } else {
 setToast({ message: detail, type: "error" });
 }
 } finally {
 setSubmitting(false);
 }
 };

 // Filter state dropdown to creator's scope
 const scopedStatesList = (() => {
  if (userRole === "SPL" && creatorStates) {
   const ids = new Set(creatorStates.map((s) => s.id));
   return statesList.filter((s) => ids.has(s.id));
  }
  if (userRole === "PL" && creatorDistricts) {
   const ids = new Set(creatorDistricts.map((d) => d.state_id));
   return statesList.filter((s) => ids.has(s.id));
  }
  if (userRole === "PM" && creatorBlocks) {
   const ids = new Set(creatorBlocks.map((b) => b.state_id));
   return statesList.filter((s) => ids.has(s.id));
  }
  return statesList;
 })();

 // Search/filtering for the three inline assignment pickers now lives inside
 // ChipMultiSelect itself, over the already-loaded districtsList/blocksList/schoolsList.

 const renderTableContent = () => {
 if (loading) {
 return (
 <div className="p-6 space-y-4">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="flex items-center gap-4 animate-pulse">
 <div className="w-8 h-4 bg-[#e5e7eb] rounded" />
 <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-32 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-16 h-4 bg-[#e5e7eb] rounded" />
 </div>
 ))}
 </div>
 );
 }

 if (data.length === 0) {
 return (
 <div className="text-center py-16 px-5">
 <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineUserGroup className="text-2xl text-[#8b949e]" />
 </div>
 <p className="m-0 text-sm font-medium text-[#57606a]">
 {search ? "No users match your search." : "No users found."}
 </p>
 <p className="m-0 text-xs text-[#8b949e] mt-1">
 {search ? "Try a different search term." : "Create your first user to get started."}
 </p>
 </div>
 );
 }

 return (
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
 <th className="py-4 px-4 w-[44px]">
 <input
 type="checkbox"
 className="accent-[#0078d4] w-4 h-4 cursor-pointer"
 checked={isAllSelected}
 onChange={toggleSelectAll}
 />
 </th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[60px]">#</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Username</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Name</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Email</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Role</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Status</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[#e5e7eb]">
 {data.map((item, idx) => (
 <tr key={item.id} className="group transition-colors duration-150 hover:bg-[#f6f8fa] cursor-pointer" onClick={() => openEditModal(item)}>
 <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
 {canManageUser(item.role) ? (
 <input
 type="checkbox"
 className="accent-[#0078d4] w-4 h-4 cursor-pointer"
 checked={selectedIds.includes(item.id)}
 onChange={() => toggleSelect(item.id)}
 />
 ) : (
 <span className="w-4 h-4 inline-block" />
 )}
 </td>
 <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{(currentPage - 1) * pageSize + idx + 1}</td>
 <td className="py-4 px-6">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 bg-[#eff6ff] rounded-full flex items-center justify-center flex-shrink-0">
 <span className="text-xs font-bold text-[#0078d4] uppercase">
 {(item.username || "U")[0]}
 </span>
 </div>
 <span className="font-medium text-[#24292f]">{item.username}</span>
 </div>
 </td>
 <td className="py-4 px-6 text-[#24292f]">
 {[item.first_name, item.last_name].filter(Boolean).join(" ") || "—"}
 </td>
 <td className="py-4 px-6 text-[#57606a]">{item.email || "—"}</td>
 <td className="py-4 px-6">
 {item.role ? (
 <span
 className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded ${
 ROLE_COLORS[item.role] || "bg-[#f3f4f6] text-[#57606a]"
 }`}
 >
 {item.role}
 </span>
 ) : (
 <span className="text-[#8b949e] text-xs">—</span>
 )}
 </td>
 <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
 <button
 className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded border-none cursor-pointer transition-all duration-200 ${
 item.enabled
 ? "bg-green-50 text-green-700 hover:bg-green-100"
 : "bg-[#f3f4f6] text-[#8b949e] hover:bg-[#e5e7eb]"
 } disabled:opacity-50 disabled:cursor-not-allowed`}
 title={item.enabled ? "Click to deactivate" : "Click to activate"}
 disabled={togglingStatusId === item.id}
 onClick={() => handleToggleStatus(item.id, item.enabled)}
 >
 {togglingStatusId === item.id ? (
 <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 ) : (
 <span
 className={`w-1.5 h-1.5 rounded-full ${
 item.enabled ? "bg-green-500" : "bg-[#8b949e]"
 }`}
 />
 )}
 {item.enabled ? "Active" : "Inactive"}
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 );
 };

 return (
 <div className="p-6">
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 />
 )}

 {/* ── LIST VIEW ── */}
 {view === "list" && (
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
 {/* Toolbar */}
 <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
 <div className="relative flex items-center flex-1 max-w-sm">
 <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
 <input
 type="text"
 className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 placeholder="Search users…"
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 {search && (
 <button
 className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
 onClick={() => setSearch("")}
 >
 <HiOutlineX className="text-sm" />
 </button>
 )}
 </div>
 <div className="flex items-center gap-2">
 <FilterDropdown
 filters={[
 {
 key: "role",
 label: "Role",
 type: "select",
 options: ROLE_OPTIONS.map((r) => ({ value: r, label: r })),
 },
 {
 key: "enabled",
 label: "Status",
 type: "select",
 options: [
 { value: "true", label: "Active" },
 { value: "false", label: "Inactive" },
 ],
 },
 // State/District/Block/School are independent of each other and of Role
 // for the actual query — you can filter by District alone with no State
 // picked. But once a parent IS picked, the child's option LIST narrows to
 // it (State=UP -> District only offers UP's districts) so you're not
 // offered combinations that can only ever return zero results.
 {
 key: "state_id",
 label: "State",
 type: "select",
 options: statesList.map((s) => ({ value: String(s.id), label: s.name })),
 },
 {
 key: "district_id",
 label: "District",
 type: "async-select",
 pageSize: 20,
 scopeKey: filterDraft.state_id ? `state:${filterDraft.state_id}` : "all",
 fetchFn: (search, page) => fetchMasterData("districts", {
 page, page_size: 20, search,
 state_id: filterDraft.state_id || undefined,
 }).then((res) => ({ data: (res.data || []).map((d) => ({ value: String(d.id), label: d.name })), total_count: res.total_count || 0 })),
 },
 {
 key: "block_id",
 label: "Block",
 type: "async-select",
 pageSize: 20,
 scopeKey: filterDraft.district_id ? `district:${filterDraft.district_id}` : "all",
 fetchFn: (search, page) => fetchMasterData("blocks", {
 page, page_size: 20, search,
 district_id: filterDraft.district_id || undefined,
 }).then((res) => ({ data: (res.data || []).map((b) => ({ value: String(b.id), label: b.name })), total_count: res.total_count || 0 })),
 },
 {
 key: "school_id",
 label: "School",
 type: "async-select",
 pageSize: 20,
 // Nationwide schools list is large (thousands of rows) — load it lazily,
 // page by page as the user scrolls, scoped to whatever Block/District/
 // State is already picked (same precedence District/Block use above).
 // scopeKey changes (rather than fetchFn's identity, which changes every
 // render) are what the dropdown treats as "the results are now stale,
 // start over from page 1".
 scopeKey: filterDraft.block_id
 ? `block:${filterDraft.block_id}`
 : filterDraft.district_id
 ? `district:${filterDraft.district_id}`
 : filterDraft.state_id
 ? `state:${filterDraft.state_id}`
 : "all",
 fetchFn: (search, page) => fetchSchools({
 page, page_size: 20, search,
 block_id: filterDraft.block_id || undefined,
 district_id: !filterDraft.block_id ? (filterDraft.district_id || undefined) : undefined,
 state_id: !filterDraft.block_id && !filterDraft.district_id ? (filterDraft.state_id || undefined) : undefined,
 }).then((res) => ({ data: (res.data || []).map((s) => ({ value: String(s.id), label: s.name })), total_count: res.total_count || 0 })),
 },
 ]}
 values={filters}
 onChange={(newFilters) => {
 setFilters(newFilters);
 setCurrentPage(1);
 }}
 onDraftChange={(draft) => setFilterDraft(draft)}
 />
 {selectedIds.length > 0 && allowedRoles.length > 0 && (
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border border-red-200 rounded-xl cursor-pointer transition-all duration-200 bg-red-50 text-red-700 hover:bg-red-100"
 onClick={openDeleteBulk}
 >
 <HiOutlineTrash className="text-base" />
 Delete Selected ({selectedIds.length})
 </button>
 )}
 {canBulkUploadUsers && (
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border border-[#d0d5dd] rounded-xl cursor-pointer transition-all duration-200 bg-white text-[#24292f] hover:bg-[#f3f4f6]"
 onClick={() => {
 setBulkUploadFile(null);
 setBulkUploadResult(null);
 setShowBulkUploadModal(true);
 }}
 >
 <HiOutlineUpload className="text-base" />
 Bulk Upload
 </button>
 )}
 {canCreateUsers && (
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-xl cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
 onClick={openCreate}
 >
 <HiOutlinePlus className="text-base" />
 Create User
 </button>
 )}
 </div>
 </div>

 {/* Table */}
 {renderTableContent()}

 {/* Pagination */}
 {!loading && totalCount > 0 && (
 <Pagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={totalCount}
 pageSize={pageSize}
 onPageChange={(p) => loadData(p, debouncedSearch, filters)}
 onPageSizeChange={handlePageSizeChange}
 entityName="users"
 />
 )}
 </div>
 )}

 {/* Create/Edit User Form — also doubles as the read-only "detail" view: when the
     viewer can't manage the target user's role, every field is disabled and the
     Save button is hidden (see isReadOnly below). */}
 {view === "form" && (() => {
 const isReadOnly = !!editingUserId && !canManageUser(originalRole);
 return (
 <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
 {/* Form Header */}
 <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
 <div className="flex items-center gap-3">
 <button
  onClick={() => { setView("list"); setEditingUserId(null); }}
  className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
  title="Back"
 >
  <HiOutlineChevronLeft className="text-lg" />
 </button>
 <div>
 <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">
 {isReadOnly ? "User Details" : editingUserId ? "Edit User" : "Create New User"}
 </h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
 {isReadOnly ? "View user information" : editingUserId ? "Update user profile details" : "Add a new user and assign a role"}
 </p>
 </div>
 </div>
 {editingUserId && canManageUser(originalRole) && (
 <button
 type="button"
 onClick={() => openDeleteSingle(editingUserId)}
 className="py-2 px-4 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
 >
 Delete
 </button>
 )}
 </div>

 <form onSubmit={handleSubmit} className="flex flex-col">
 <div className="p-6 space-y-4">
 {/* First Name & Last Name */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label
 htmlFor="user-first-name"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 First Name <span className="text-red-500">*</span>
 </label>
 <input
 id="user-first-name"
 className={`w-full py-2.5 px-4 text-sm border rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 ${
 formErrors.first_name ? "border-red-400" : "border-[#d0d5dd]"
 }`}
 type="text"
 placeholder="Enter first name"
 value={form.first_name}
 onChange={(e) => handleChange("first_name", e.target.value)}
 disabled={isReadOnly}
 />
 {formErrors.first_name && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.first_name}</p>
 )}
 </div>
 <div>
 <label
 htmlFor="user-last-name"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Last Name <span className="text-red-500">*</span>
 </label>
 <input
 id="user-last-name"
 className={`w-full py-2.5 px-4 text-sm border rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 ${
 formErrors.last_name ? "border-red-400" : "border-[#d0d5dd]"
 }`}
 type="text"
 placeholder="Enter last name"
 value={form.last_name}
 onChange={(e) => handleChange("last_name", e.target.value)}
 disabled={isReadOnly}
 />
 {formErrors.last_name && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.last_name}</p>
 )}
 </div>
 </div>

 {/* Username — hidden in edit mode */}
 {!editingUserId && (
 <div>
 <label
 htmlFor="user-username"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Username <span className="text-red-500">*</span>
 </label>
 <input
 id="user-username"
 className={`w-full py-2.5 px-4 text-sm border rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 ${
 formErrors.username ? "border-red-400" : "border-[#d0d5dd]"
 }`}
 type="text"
 placeholder="Enter username"
 value={form.username}
 onChange={(e) => handleChange("username", e.target.value)}
 disabled={isReadOnly}
 />
 {formErrors.username && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.username}</p>
 )}
 </div>
 )}

 {/* Email — shown alongside a readonly Username in edit mode (Username's
 own editable field above is create-mode only, so this is the only place
 edit mode shows it) */}
 <div className={editingUserId ? "grid grid-cols-2 gap-4" : undefined}>
 {editingUserId && (
 <div>
 <label
 htmlFor="user-username-readonly"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Username
 </label>
 <input
 id="user-username-readonly"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#57606a] bg-[#f6f8fa] box-border cursor-not-allowed"
 type="text"
 value={form.username}
 disabled
 />
 </div>
 )}
 <div>
 <label
 htmlFor="user-email"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Email <span className="text-red-500">*</span>
 </label>
 <input
 id="user-email"
 className={`w-full py-2.5 px-4 text-sm border rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 ${
 formErrors.email ? "border-red-400" : "border-[#d0d5dd]"
 }`}
 type="email"
 placeholder="Enter email address"
 value={form.email}
 onChange={(e) => handleChange("email", e.target.value)}
 disabled={isReadOnly}
 />
 {formErrors.email && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.email}</p>
 )}
 </div>
 </div>

 {/* Password — hidden in edit mode */}
 {!editingUserId && (
 <div>
 <label
 htmlFor="user-password"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Temporary Password <span className="text-red-500">*</span>
 </label>
 <input
 id="user-password"
 className={`w-full py-2.5 px-4 text-sm border rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 ${
 formErrors.password ? "border-red-400" : "border-[#d0d5dd]"
 }`}
 type="password"
 placeholder="Enter temporary password"
 value={form.password}
 onChange={(e) => handleChange("password", e.target.value)}
 disabled={isReadOnly}
 />
 {formErrors.password && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.password}</p>
 )}
 </div>
 )}

 {/* Role */}
 <div>
 <label
 htmlFor="user-role"
 className="block mb-1.5 text-sm font-medium text-[#24292f]"
 >
 Role <span className="text-red-500">*</span>
 </label>
 <SearchableDropdown
 value={form.role || ""}
 onChange={(id) => handleChange("role", id)}
 placeholder="Select Role"
 fetchFn={staticFetchFn(allowedRoles.map((r) => ({ id: r, name: r })))}
 resolveFn={(id) => Promise.resolve({ id, name: id })}
 cacheNamespace="user_role"
 variant="form"
 disabled={isReadOnly}
 />
 {formErrors.role && (
 <p className="m-0 mt-1 text-xs text-red-500">{formErrors.role}</p>
 )}
 </div>

 {/* State assignment for SPL (create + edit) */}
 {form.role === "SPL" && (
 <div>
  <label className="block mb-1 text-sm font-medium text-[#24292f]">Assign State <span className="text-red-500">*</span></label>
  {userRole === "SPL" && creatorStates?.length > 0 ? (
  <input
   className="w-full py-2.5 px-4 text-sm border rounded-md text-[#57606a] bg-[#f6f8fa] border-[#d0d5dd] cursor-not-allowed"
   value={creatorStates[0].name}
   disabled
   readOnly
  />
  ) : (
  <SearchableDropdown
   value={String(form.state_id || "")}
   onChange={(id) => handleChange("state_id", id)}
   placeholder="Select State"
   fetchFn={scopedStatesFetchFn(userRole === "ADMIN", scopedStatesList)}
   cacheNamespace="user_state_id"
   resolveFn={resolveStateLabel}
   variant="form"
   disabled={isReadOnly}
  />
  )}
  {formErrors.state_id && (
  <p className="m-0 mt-1 text-xs text-red-500">{formErrors.state_id}</p>
  )}
 </div>
 )}

 {/* State + Districts assignment for PL (create + edit) */}
 {form.role === "PL" && (
 <div className="space-y-4">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
   <div>
   <label className="block mb-1 text-sm font-medium text-[#24292f]">State <span className="text-red-500">*</span> <span className="text-[#57606a] font-normal text-xs">(filter districts)</span></label>
   {userRole === "SPL" && creatorStates?.length > 0 ? (
    <input
    className="w-full py-2.5 px-4 text-sm border rounded-md text-[#57606a] bg-[#f6f8fa] border-[#d0d5dd] cursor-not-allowed"
    value={creatorStates[0].name}
    disabled
    readOnly
    />
   ) : (
    <SearchableDropdown
     value={String(form.state_id || "")}
     onChange={(id) => handleChange("state_id", id)}
     placeholder="Select State"
     fetchFn={scopedStatesFetchFn(userRole === "ADMIN", scopedStatesList)}
     cacheNamespace="user_state_id"
     resolveFn={resolveStateLabel}
     variant="form"
     disabled={isReadOnly}
    />
   )}
   {formErrors.state_id && (
   <p className="m-0 mt-1 text-xs text-red-500">{formErrors.state_id}</p>
   )}
   </div>
   <div>
   <label className="block mb-1 text-sm font-medium text-[#24292f]">
    Assign Districts <span className="text-red-500">*</span>
   </label>
   {!form.state_id ? (
    <div className="border border-[#d0d5dd] rounded-md bg-white">
     <p className="text-sm text-[#57606a] px-3 py-2">Select a state first</p>
    </div>
   ) : (
    <ChipMultiSelect
     options={districtsList}
     selectedIds={form.district_ids}
     onAdd={(id) => handleChange("district_ids", [...form.district_ids, id])}
     onRemove={(id) => handleChange("district_ids", form.district_ids.filter((x) => x !== id))}
     placeholder="Search districts…"
     emptyMessage="No districts available"
     disabled={isReadOnly}
    />
   )}
   {formErrors.district_ids && (
   <p className="m-0 mt-1 text-xs text-red-500">{formErrors.district_ids}</p>
   )}
   </div>
  </div>
 </div>
 )}

 {/* Location fields: State→Districts(multi)→Blocks(multi, union across districts) for both
     PM and PO; PO additionally cascades to Assign Schools (multi, union across blocks). */}
 {(form.role === "PM" || form.role === "PO") && (
 <div className="space-y-4">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
   <div>
    <label className="block mb-1 text-sm font-medium text-[#24292f]">State <span className="text-red-500">*</span></label>
    <SearchableDropdown
     value={String(form.state_id || "")}
     onChange={(id) => handleChange("state_id", id)}
     placeholder="Select State"
     fetchFn={scopedStatesFetchFn(userRole === "ADMIN", scopedStatesList)}
     cacheNamespace="user_state_id"
     resolveFn={resolveStateLabel}
     variant="form"
     disabled={isReadOnly}
    />
    {formErrors.state_id && (
    <p className="m-0 mt-1 text-xs text-red-500">{formErrors.state_id}</p>
    )}
   </div>

   {/* Districts multi-select for PM and PO (create + edit) — same picker pattern as
       PL's "Assign Districts". Removing a district also drops any of its blocks
       already in form.block_ids, so a removed district can't leave orphaned blocks
       still assigned (and for PO, removing a block below does the same one level
       further down for schools). */}
   {(form.role === "PM" || form.role === "PO") && (
    <div>
     <label className="block mb-1 text-sm font-medium text-[#24292f]">
      Assign Districts <span className="text-red-500">*</span> <span className="text-[#57606a] font-normal text-xs">(filter blocks)</span>
     </label>
     {!form.state_id ? (
      <div className="border border-[#d0d5dd] rounded-md bg-white">
       <p className="text-sm text-[#57606a] px-3 py-2">Select a state first</p>
      </div>
     ) : (
      <ChipMultiSelect
       options={districtsList}
       selectedIds={form.district_ids}
       onAdd={(id) => handleChange("district_ids", [...form.district_ids, id])}
       onRemove={(id) => {
        const orphanedBlockIds = new Set(
         blocksList.filter((b) => b.district_id === id).map((b) => b.id)
        );
        setForm((prev) => ({
         ...prev,
         district_ids: prev.district_ids.filter((x) => x !== id),
         block_ids: prev.block_ids.filter((bid) => !orphanedBlockIds.has(bid)),
        }));
       }}
       placeholder="Search districts…"
       emptyMessage="No districts available"
       disabled={isReadOnly}
      />
     )}
     {formErrors.district_ids && (
     <p className="m-0 mt-1 text-xs text-red-500">{formErrors.district_ids}</p>
     )}
    </div>
   )}
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
   {/* Blocks multi-select for PM and PO (create + edit) — union across every
       selected district (see the blocks-loading effect above). For PO, removing
       a block also drops any of its schools already in form.school_ids. */}
   {(form.role === "PM" || form.role === "PO") && (
    <div>
     <label className="block mb-1 text-sm font-medium text-[#24292f]">
      Assign Blocks <span className="text-red-500">*</span>{form.role === "PO" && <span className="text-[#57606a] font-normal text-xs"> (filter schools)</span>}
     </label>
     {!form.district_ids.length ? (
      <div className="border border-[#d0d5dd] rounded-md bg-white">
       <p className="text-sm text-[#57606a] px-3 py-2">Select at least one district first</p>
      </div>
     ) : (
      <ChipMultiSelect
       options={blocksList}
       selectedIds={form.block_ids}
       getLabel={(b) => {
        const districtName = districtsList.find((d) => d.id === b.district_id)?.name;
        return `${b.name}${districtName ? ` (${districtName})` : ""}`;
       }}
       onAdd={(id) => handleChange("block_ids", [...form.block_ids, id])}
       onRemove={(id) => {
        if (form.role !== "PO") {
         handleChange("block_ids", form.block_ids.filter((x) => x !== id));
         return;
        }
        const orphanedSchoolIds = new Set(
         schoolsList.filter((s) => s.block_id === id).map((s) => s.id)
        );
        setForm((prev) => ({
         ...prev,
         block_ids: prev.block_ids.filter((x) => x !== id),
         school_ids: prev.school_ids.filter((sid) => !orphanedSchoolIds.has(sid)),
        }));
       }}
       placeholder="Search blocks…"
       emptyMessage="No blocks available"
       disabled={isReadOnly}
      />
     )}
     {formErrors.block_ids && (
     <p className="m-0 mt-1 text-xs text-red-500">{formErrors.block_ids}</p>
     )}
    </div>
   )}

   {/* Schools multi-select for PO (create + edit) — union across every selected block */}
   {form.role === "PO" && (
    <div>
     <label className="block mb-1 text-sm font-medium text-[#24292f]">
      Assign Schools <span className="text-red-500">*</span> <span className="text-[#57606a] font-normal text-xs">(adopted schools only)</span>
     </label>
     {!form.block_ids.length ? (
      <div className="border border-[#d0d5dd] rounded-md bg-white">
       <p className="text-sm text-[#57606a] px-3 py-2">Select at least one block first</p>
      </div>
     ) : (
      <ChipMultiSelect
       options={schoolsList}
       selectedIds={form.school_ids}
       onAdd={(id) => handleChange("school_ids", [...form.school_ids, id])}
       onRemove={(id) => handleChange("school_ids", form.school_ids.filter((x) => x !== id))}
       placeholder="Search schools…"
       emptyMessage="No adopted schools available"
       getLabel={(s) => {
        const blockName = blocksList.find((b) => b.id === s.block_id)?.name;
        return `${s.name}${blockName ? ` (${blockName})` : ""}`;
       }}
       getSearchText={(s) => `${s.name} ${s.udise_code || ""}`}
       disabled={isReadOnly}
      />
     )}
     {formErrors.school_ids && (
     <p className="m-0 mt-1 text-xs text-red-500">{formErrors.school_ids}</p>
     )}
    </div>
   )}
  </div>
 </div>
 )}
 </div>

 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-xl">
 <button
 type="button"
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={() => { setView("list"); setEditingUserId(null); }}
 disabled={submitting}
 >
 {isReadOnly ? "Back" : "Cancel"}
 </button>
 {!isReadOnly && (
 <button
 type="submit"
 className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 disabled={submitting || !isLocationComplete()}
 >
 {submitting ? (
 <>
 <svg
 className="animate-spin h-4 w-4"
 viewBox="0 0 24 24"
 fill="none"
 >
 <circle
 className="opacity-25"
 cx="12"
 cy="12"
 r="10"
 stroke="currentColor"
 strokeWidth="4"
 />
 <path
 className="opacity-75"
 fill="currentColor"
 d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
 />
 </svg>
 {editingUserId ? "Saving…" : "Creating…"}
 </>
 ) : (
 editingUserId ? "Save Changes" : "Create User"
 )}
 </button>
 )}
 </div>
 </form>
 </div>
 );
 })()}

 {/* Delete Confirmation Modal */}
 {showDeleteModal && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
 onClick={() => !deleting && setShowDeleteModal(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => {
 if (e.key === "Escape" && !deleting) setShowDeleteModal(false);
 }}
 >
 <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
 <div>
 <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">Confirm Deletion</h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
 This action cannot be undone
 </p>
 </div>
 <button
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
 onClick={() => setShowDeleteModal(false)}
 disabled={deleting}
 >
 <HiOutlineX className="text-lg" />
 </button>
 </div>

 <div className="p-6">
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
 <HiOutlineTrash className="text-lg text-red-600" />
 </div>
 <div>
 <p className="m-0 text-sm text-[#24292f]">
 {deleteTarget
 ? "Are you sure you want to delete this user?"
 : `Are you sure you want to delete ${selectedIds.length} selected user(s)?`}
 </p>
 <p className="m-0 mt-2 text-xs text-[#8b949e]">
 The user(s) will be permanently removed from both Keycloak and the local database.
 </p>
 </div>
 </div>
 </div>

 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 type="button"
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={() => setShowDeleteModal(false)}
 disabled={deleting}
 >
 Cancel
 </button>
 <button
 type="button"
 className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={confirmDelete}
 disabled={deleting}
 >
 {deleting ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Deleting…
 </>
 ) : (
 "Delete"
 )}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Bulk Upload PM Modal */}
 {showBulkUploadModal && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
 onClick={() => !bulkUploading && setShowBulkUploadModal(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => {
 if (e.key === "Escape" && !bulkUploading) setShowBulkUploadModal(false);
 }}
 >
 {/* Header */}
 <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
 <div>
 <h3 className="m-0 text-base font-semibold text-[#24292f]">Bulk Upload Users</h3>
 <p className="m-0 mt-0.5 text-xs text-[#8b949e]">Upload an Excel file (.xlsx) to create users of any role</p>
 </div>
 <button
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
 onClick={() => setShowBulkUploadModal(false)}
 disabled={bulkUploading}
 >
 <HiOutlineX className="text-lg" />
 </button>
 </div>

 {/* Body */}
 <div className="p-6 space-y-5">
 {/* Template Download */}
 <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
 <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
 <div className="flex-1">
 <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
 <p className="m-0 text-xs text-[#57606a] mt-0.5">
 Required: first_name, last_name, username, email, role. state, districts, blocks and
 school_udise_codes are required to the depth each role manages — SPL needs state,
 PL needs state + districts, PM needs state + districts + blocks, PO needs state +
 districts + blocks + school_udise_codes. ADMIN needs none. Comma-separate multiple values.
 </p>
 </div>
 <button
 className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={() => {
 const { downloadXlsxTemplate } = require("../services/xlsxTemplate");
 downloadXlsxTemplate(
 "user_upload_template.xlsx",
 ["first_name", "last_name", "username", "email", "role", "password", "state", "districts", "blocks", "school_udise_codes"],
 [
 ["Rahul", "Sharma", "rahul_spl", "rahul@test.com", "SPL", "", "Rajasthan", "", "", ""],
 ["Amit", "Kumar", "amit_pl", "amit@test.com", "PL", "", "Madhya Pradesh", "Bhopal, Indore", "", ""],
 ["Neha", "Verma", "neha_pm", "neha@test.com", "PM", "", "Rajasthan", "", "Jaipur Block A", ""],
 ["Suresh", "Rao", "suresh_po", "suresh@test.com", "PO", "", "", "", "", "21010100101, 21010100203"],
 ]
 );
 }}
 >
 <HiOutlineDownload className="text-sm" />
 Download
 </button>
 </div>

 {/* Note about password */}
 <p className="m-0 text-xs text-amber-600">
 Leave the password column blank to auto-generate a temporary password. Users will be
 asked to change it on first login.
 </p>

 {/* File Input */}
 {!bulkUploadResult && (
 <div>
 <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
 <div
 className="relative border-2 border-dashed border-[#d0d5dd] rounded-md p-6 text-center hover:border-[#0078d4] transition-colors cursor-pointer"
 onClick={() => document.getElementById('user-bulk-file-input')?.click()}
 >
 <input
 id="user-bulk-file-input"
 type="file"
 accept=".xlsx"
 className="hidden"
 onChange={(e) => setBulkUploadFile(e.target.files?.[0] || null)}
 />
 <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
 {bulkUploadFile ? (
 <p className="m-0 text-sm font-medium text-[#24292f]">{bulkUploadFile.name}</p>
 ) : (
 <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
 )}
 <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB · Max 1000 rows</p>
 </div>
 </div>
 )}

 {/* Upload Result */}
 {bulkUploadResult && (
 <div className="space-y-4">
 {/* Summary Cards */}
 <div className="grid grid-cols-3 gap-3">
 <div className="p-3 rounded-lg bg-[#f6f8fa] border border-[#e5e7eb] text-center">
 <p className="m-0 text-2xl font-bold text-[#24292f]">{bulkUploadResult.total}</p>
 <p className="m-0 text-xs text-[#8b949e] mt-1">Total</p>
 </div>
 <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
 <p className="m-0 text-2xl font-bold text-green-700">{bulkUploadResult.success}</p>
 <p className="m-0 text-xs text-green-600 mt-1">Success</p>
 </div>
 <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
 <p className="m-0 text-2xl font-bold text-red-700">{bulkUploadResult.failed}</p>
 <p className="m-0 text-xs text-red-600 mt-1">Failed</p>
 </div>
 </div>

 {/* Errors List */}
 {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
 <div>
 <p className="m-0 mb-2 text-sm font-medium text-[#24292f]">Errors</p>
 <div className="max-h-48 overflow-y-auto border border-[#e5e7eb] rounded-lg divide-y divide-[#e5e7eb]">
 {bulkUploadResult.errors.map((err, i) => (
 <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs">
 <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-red-50 text-red-700 font-semibold flex-shrink-0">
 {err.row}
 </span>
 <span className="text-[#57606a]">{err.error}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
 onClick={() => {
 setShowBulkUploadModal(false);
 setBulkUploadFile(null);
 setBulkUploadResult(null);
 }}
 disabled={bulkUploading}
 >
 {bulkUploadResult ? "Close" : "Cancel"}
 </button>
 {!bulkUploadResult && (
 <button
 className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 disabled={!bulkUploadFile || bulkUploading}
 onClick={async () => {
 if (!bulkUploadFile) return;
 setBulkUploading(true);
 try {
 const result = await bulkUploadUsers(bulkUploadFile);
 setBulkUploadResult(result);
 if (result.success > 0) {
 loadData(currentPage, debouncedSearch, filters);
 }
 } catch (err) {
 const detail = err.response?.data?.detail || "Bulk upload failed. Please try again.";
 setToast({ message: detail, type: "error" });
 } finally {
 setBulkUploading(false);
 }
 }}
 >
 {bulkUploading ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Uploading…
 </>
 ) : (
 <>
 <HiOutlineUpload className="text-base" />
 Upload
 </>
 )}
 </button>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

export default Users;
